import { Inject, Injectable } from '@angular/core';
import { ENV_CONFIG } from '@digilize/shared/utils/tokens';
import { EnvConfig } from '@shared/definitions';

import type { ApplicationInsights } from '@microsoft/applicationinsights-web';
/** Ceiling on how long the idle request may wait before the SDK is fetched anyway. */
const IDLE_TIMEOUT_MS = 3000;
/** An error loop before the SDK is up must not grow without bound. */
const MAX_PENDING = 50;

/**
 * Application Insights is fetched in a chunk of its own once the browser goes quiet after bootstrap,
 * instead of riding in the initial bundle. `import type` is what makes that split possible: the SDK is
 * ~470kB of source (~10% of an app's initial bundle) and `ErrorHandler` is constructed during
 * bootstrap, so a value import put the whole SDK — its parse, its compile and its init — in front of
 * the first paint of every app in the repo.
 *
 * What is logged does not change; when it starts does. Calls made before the SDK is up are queued and
 * flushed on load, auto route tracking opens on the URL the router settled on rather than the one the
 * document was served with, and the boot API wave is no longer captured as dependencies — in the shop
 * those reads are issued by the index.html prefetch before any Angular code runs, so the SDK never saw
 * them anyway.
 */
@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  appInsights: ApplicationInsights;

  private pending: Array<(appInsights: ApplicationInsights) => void> = [];
  private loadStarted = false;

  constructor(@Inject(ENV_CONFIG) private envConfig: EnvConfig) {
    if (this.enabled) {
      this.whenIdle(() => this.load());
    }
  }

  logPageView(name?: string, url?: string) {
    // option to call manually
    this.track((appInsights) =>
      appInsights.trackPageView({
        name: name,
        uri: url,
      })
    );
  }

  logEvent(name: string, properties?: { [key: string]: any }) {
    this.track((appInsights) => appInsights.trackEvent({ name: name }, properties));

    const clarity = (window as any)?.clarity;
    if (clarity) {
      clarity('event', name);
      if (properties) {
        Object.keys(properties).forEach((key) => clarity('set', key, String(properties[key])));
      }
    }
  }

  logMetric(name: string, average: number, properties?: { [key: string]: any }) {
    this.track((appInsights) => appInsights.trackMetric({ name: name, average: average }, properties));
  }

  logException(exception: Error, severityLevel?: number) {
    this.track((appInsights) => appInsights.trackException({ exception: exception, severityLevel: severityLevel }));

    const clarity = (window as any)?.clarity;
    if (clarity) {
      clarity('event', exception?.name ?? 'Exception');
      clarity('set', 'error', String(exception?.message ?? exception).substring(0, 255));
    }
  }

  logTrace(message: string, properties?: { [key: string]: any }) {
    this.track((appInsights) => appInsights.trackTrace({ message: message }, properties));
  }

  private get enabled(): boolean {
    return this.envConfig.instrumentationKey !== '';
  }

  /** Sends now if the SDK is up, otherwise queues and pulls the chunk in ahead of the idle callback. */
  private track(send: (appInsights: ApplicationInsights) => void): void {
    if (!this.enabled) {
      return;
    }

    if (this.appInsights) {
      send(this.appInsights);
      return;
    }

    if (this.pending.length < MAX_PENDING) {
      this.pending.push(send);
    }
    this.load();
  }

  private load(): void {
    if (this.loadStarted) {
      return;
    }
    this.loadStarted = true;

    import('@microsoft/applicationinsights-web')
      .then(({ ApplicationInsights }) => {
        const appInsights = new ApplicationInsights({
          config: {
            instrumentationKey: this.envConfig.instrumentationKey,
            enableAutoRouteTracking: true, // option to log all route changes
            enableUnhandledPromiseRejectionTracking: true, // option to track unhandled promise rejections
          },
        });
        appInsights.loadAppInsights();
        this.appInsights = appInsights;

        const queued = this.pending;
        this.pending = [];
        queued.forEach((send) => send(appInsights));
      })
      .catch(() => {
        /* Telemetry may never take the app down with it: a chunk that a blocker or a dead network kept
           out just means this session goes unreported. Not retried — the failure is caught here, so it
           never reaches GlobalErrorHandlerWithLog and its chunk-failure reload. */
        this.pending = [];
      });
  }

  private whenIdle(run: () => void): void {
    if (typeof window === 'undefined' || typeof window.requestIdleCallback !== 'function') {
      setTimeout(run, IDLE_TIMEOUT_MS);
      return;
    }

    window.requestIdleCallback(run, { timeout: IDLE_TIMEOUT_MS });
  }
}
